import React from "react";
import { formatDistance } from "tsi-common-react/src/apps/retail/helpers";
import {
    RetailStoreEventCTAType,
    RetailStoreEventType,
} from "tsi-common-react/src/constants";
import { useMountEffect } from "tsi-common-react/src/hooks/useMountEffect";
import { IRetailStoreWithDistance } from "tsi-common-react/src/models/location";
import { trackRetailStoreEvent } from "tsi-common-react/src/utils/analytics";
import { formatPhoneNumber } from "tsi-common-react/src/utils/format";
import {
    getGoogleLatLng,
    loadGMaps,
    newMap,
} from "tsi-common-react/src/utils/maps";
import { t } from "ttag";

import iconClock from "../../img/retail-locator/icon-clock.png";
import iconDriving from "../../img/retail-locator/icon-driving.png";
import iconMarker2 from "../../img/retail-locator/icon-marker2.png";
import iconPhone from "../../img/retail-locator/icon-phone.png";
import locationIcon from "../../img/retail-locator/location-icon.png";
import { CMSFlagshipStorePage } from "../models";

// Initialize Google Maps with store location and marker
async function initializeMap(
    mapMountElement: HTMLElement,
    store: IRetailStoreWithDistance,
): Promise<void> {
    // Load Google Maps API
    await loadGMaps();
    const position =
        store.lat && store.lng
            ? getGoogleLatLng({
                  lat: store.lat,
                  lng: store.lng,
              })
            : undefined;
    const retailMap = newMap(mapMountElement, {
        center: position,
        zoom: 16,
        scrollwheel: false,
        draggable: true,
        disableDefaultUI: false,
        clickableIcons: false,
        mapTypeId: google.maps.MapTypeId.ROADMAP,
    });
    const marker = new google.maps.Marker({
        position: position,
        icon: iconMarker2,
        // TODO(i18n): Use t tagged template: t`Tempur-Pedic Store`
        title: "Tempur-Pedic Store",
    });
    marker.setMap(retailMap);
}

// Format store address for Google Maps directions URL
function formatDestinationAddress(store: IRetailStoreWithDistance): string {
    return encodeURI(
        store.name +
            " " +
            store.address +
            " " +
            store.address2 +
            " " +
            store.city +
            "," +
            store.state +
            " " +
            store.postal,
    );
}

// Monitor data layer events when clicking on store location details like directions, phone numbers.
function handleLocationInfoClick(
    store: IRetailStoreWithDistance,
    ctaType: RetailStoreEventCTAType,
    textContent: string | null,
    event: React.MouseEvent<HTMLElement>,
): void {
    event.stopPropagation();
    const element = event.currentTarget as HTMLElement;
    const linkText = textContent || element.textContent.trim();
    const linkUrl = element.getAttribute("href");
    trackRetailStoreEvent(
        RetailStoreEventType.TEMPUR_STORE,
        store,
        ctaType,
        linkText,
        linkUrl,
    );
}

export function FlagshipStoreInfoMap(props: {
    mapMountElement: HTMLElement;
    cmsProps: CMSFlagshipStorePage;
    store: IRetailStoreWithDistance;
}): React.ReactNode {
    useMountEffect(() => {
        void initializeMap(props.mapMountElement, props.store);
    });

    // Convert distance from meters to miles
    const phoneInUSFormat = props.store.phone
        ? formatPhoneNumber(props.store.phone)
        : "";
    // encode destination address for Directions link
    const destinationAddress = formatDestinationAddress(props.store);

    return (
        <>
            <div className="location__distance">
                <div className="location__distance-text">
                    <span className="location__distance-text--large">
                        {formatDistance(props.store.distance)}
                    </span>
                    <br />
                    <span className="location__distance-text--small">
                        {t`miles`}
                    </span>
                </div>
            </div>
            <div className="location__info">
                <div className="location__header">
                    <div className="location__name1">
                        {props.cmsProps.title}
                    </div>
                    <div className="location__name2">
                        {props.cmsProps.shopping_district}
                    </div>
                </div>
                <div className="location__location">
                    <span className="location__icon">
                        <img alt={t`Location`} src={locationIcon} />
                    </span>
                    <div className="location__address">
                        <div className="location__address">
                            {props.cmsProps.address}
                            <br />
                            {props.cmsProps.city}, {props.cmsProps.state}{" "}
                            {props.cmsProps.postal}
                        </div>
                    </div>
                    <div className="location__nearby">
                        {props.cmsProps.located_near}
                    </div>
                </div>
                <div className="location__contact">
                    <div className="location__item location__hours">
                        <span className="location__icon">
                            <img alt={t`Hours`} src={iconClock} />
                        </span>
                        <span className="location__tooltip-trigger">
                            {props.cmsProps.hours_summary_abbr
                                .split(",")
                                .map((hour, index) => (
                                    <span key={`key-${index}`}>{hour}</span>
                                ))}
                        </span>
                    </div>
                    <div className="location__item location__directions">
                        <span className="location__icon">
                            <img alt={t`Directions`} src={iconDriving} />
                        </span>
                        <a
                            href={`https://www.google.com/maps/dir/?api=1&destination=${destinationAddress}`}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(event) =>
                                handleLocationInfoClick(
                                    props.store,
                                    RetailStoreEventCTAType.MAP_DIRECTIONS,
                                    // TODO(i18n): Use t tagged template: t`Get icon directions`
                                    "Get icon directions",
                                    event,
                                )
                            }
                        >
                            {t`Directions`}
                        </a>
                    </div>
                    <div className="location__item location__phone">
                        <span className="location__icon">
                            <img alt={t`Phone Number`} src={iconPhone} />
                        </span>
                        <a
                            href={`tel:${props.store.phone}`}
                            title={t`Phone Number`}
                            onClick={(event) =>
                                handleLocationInfoClick(
                                    props.store,
                                    RetailStoreEventCTAType.CALL,
                                    null,
                                    event,
                                )
                            }
                        >
                            {phoneInUSFormat}
                        </a>
                    </div>
                </div>
            </div>
        </>
    );
}
